/**
 * PublicWoStatus — no-auth tenant view of a Work Order.
 *
 * URL: /wo/:token  — token is minted by an admin from the WO detail page.
 * Shows: address · pipeline (queued/assigned/en_route/in_progress/done) ·
 *        assigned tech first name · ETA · timeline · click-to-call office.
 *
 * Zero login, zero PII beyond what the tenant already knows.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, ClockCountdown, Truck, Wrench, Phone, Warning, MapPin, House } from "@phosphor-icons/react";

const STAGES = [
  { key: "queued",       label: "Queued",       icon: ClockCountdown },
  { key: "assigned",     label: "Assigned",     icon: CheckCircle },
  { key: "en_route",     label: "On the way",   icon: Truck },
  { key: "in_progress",  label: "In progress",  icon: Wrench },
  { key: "done",         label: "Complete",     icon: CheckCircle },
];

export default function PublicWoStatus() {
  const { token } = useParams();
  const API = process.env.REACT_APP_BACKEND_URL;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/wo/${token}`);
        if (!r.ok) { setError((await r.json().catch(() => ({}))).detail || "Link invalid"); return; }
        setData(await r.json());
      } catch (e) { setError("Could not load status. Check your connection."); }
    })();
    // Refresh every 60s so the tenant sees live updates without pulling to refresh
    const int = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/public/wo/${token}`);
        if (r.ok) setData(await r.json());
      } catch (_) {}
    }, 60000);
    return () => clearInterval(int);
  }, [token, API]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border-2 border-slate-200 rounded-lg p-6 text-center" data-testid="public-wo-error">
          <Warning weight="fill" size={40} className="mx-auto text-amber-500"/>
          <h1 className="mt-3 font-black text-xl">Can't load this work order</h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <p className="mt-4 text-xs text-slate-500">If your landlord sent this link, please ask them to resend it.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-content-center text-slate-400 text-sm">Loading…</div>;

  const currentStageIdx = STAGES.findIndex(s => s.key === data.stage);
  const brand = data.office?.brand_color || "#0f172a";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-wo-status">
      {/* Header */}
      <div style={{ background: brand }} className="text-white">
        <div className="max-w-2xl mx-auto p-4 flex items-center gap-3">
          {data.office?.logo_url ? (
            <img src={data.office.logo_url} alt="" className="h-8 rounded bg-white/10 p-1"/>
          ) : (
            <div className="h-8 w-8 rounded bg-white/20 grid place-content-center text-xs font-black">
              {(data.office?.name || "P").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-xs opacity-80">Work order from</div>
            <div className="font-semibold truncate">{data.office?.name}</div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* WO header */}
        <div className="bg-white rounded-lg border-2 border-slate-200 p-5" data-testid="public-wo-header">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500">
            WO #{data.wo_number}
            {data.category && <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 normal-case tracking-normal">{data.category}</span>}
          </div>
          <h1 className="mt-1 font-display font-black text-2xl leading-tight">{data.title}</h1>
          <div className="mt-2 flex items-start gap-2 text-sm text-slate-700">
            <MapPin weight="bold" size={16} className="mt-0.5 shrink-0 text-slate-400"/>
            <div>
              <div className="font-semibold">{data.property_address}</div>
              {data.unit && <div className="text-slate-500">Unit {data.unit}</div>}
            </div>
          </div>
          {data.habitability && (
            <div className="mt-3 p-2 border-l-4 border-red-500 bg-red-50 text-xs text-red-800">
              <b>Habitability priority.</b> This repair is on the 30-day repair clock — we're on it.
            </div>
          )}
        </div>

        {/* Pipeline */}
        <div className="bg-white rounded-lg border-2 border-slate-200 p-5" data-testid="public-wo-pipeline">
          <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">Status</div>
          <div className="flex items-center justify-between">
            {STAGES.map((s, i) => {
              const Icon = s.icon;
              const done = i < currentStageIdx;
              const active = i === currentStageIdx;
              return (
                <div key={s.key} className="flex-1 flex flex-col items-center" data-testid={`stage-${s.key}${active ? "-active" : ""}`}>
                  <div
                    className={`h-9 w-9 rounded-full grid place-content-center border-2 transition-colors ${
                      done ? "bg-emerald-500 border-emerald-500 text-white"
                        : active ? "border-current text-white"
                        : "border-slate-300 text-slate-300 bg-white"
                    }`}
                    style={active ? { background: brand, borderColor: brand } : {}}
                  >
                    <Icon weight={active || done ? "fill" : "bold"} size={16}/>
                  </div>
                  <div className={`mt-1.5 text-[10px] font-black uppercase tracking-widest text-center leading-tight ${active ? "text-slate-900" : done ? "text-emerald-600" : "text-slate-400"}`}>
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>

          {(data.assigned_tech_first_name || data.eta) && (
            <div className="mt-4 pt-3 border-t border-slate-200 grid grid-cols-2 gap-3 text-sm">
              {data.assigned_tech_first_name && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Tech</div>
                  <div className="font-semibold" data-testid="tech-name">{data.assigned_tech_first_name}</div>
                </div>
              )}
              {data.eta && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">ETA</div>
                  <div className="font-semibold" data-testid="eta">{data.eta}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Optional message from the office */}
        {data.message_to_tenant && (
          <div className="bg-cyan-50 border-2 border-cyan-200 rounded-lg p-4">
            <div className="text-xs font-black uppercase tracking-widest text-cyan-700 mb-1">Note from your property manager</div>
            <div className="text-sm text-slate-800 whitespace-pre-wrap">{data.message_to_tenant}</div>
          </div>
        )}

        {/* Timeline */}
        {data.timeline?.length > 0 && (
          <div className="bg-white rounded-lg border-2 border-slate-200 p-5" data-testid="public-wo-timeline">
            <div className="text-xs font-black uppercase tracking-widest text-slate-500 mb-3">Timeline</div>
            <ol className="space-y-2">
              {data.timeline.map((e, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0"/>
                  <div className="flex-1">
                    <div className="text-slate-800">{e.label}</div>
                    <div className="text-[11px] text-slate-500">{new Date(e.at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Call office */}
        {data.office?.phone && (
          <a href={`tel:${data.office.phone}`} data-testid="office-call-btn"
            className="block w-full py-4 rounded-lg text-center font-black uppercase tracking-widest text-sm text-white shadow"
            style={{ background: brand }}>
            <Phone weight="fill" size={16} className="inline-block mr-1.5 -mt-0.5"/>
            Call {data.office.name} · {data.office.phone}
          </a>
        )}

        <div className="text-center text-[10px] text-slate-400 pt-2 pb-8 flex items-center justify-center gap-2 flex-wrap" data-testid="public-footer-brand">
          {data?.office?.name && <b className="text-[#0B1C2D] uppercase tracking-wider">{data.office.name}</b>}
          <span>· Powered by</span><img src="/brand/fr-lockup.png?v=10" alt="FieldRadar.pro" className="h-4 w-auto"/>
        </div>
      </div>
    </div>
  );
}
